import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { LANGUAGES, type Role } from '@mahallu/shared';
import { ApiError, auth, markUrl, useMe, useNotifications, useProfile } from '../api';
import { Wordmark } from './Brand';
import { initials } from '../format';
import { setLanguage } from '../i18n';
import { NAV, findNav, type NavGroup } from '../nav';
import { Icon } from './Icon';
import { SignOutButton } from './SignOutButton';

function groupHolds(g: NavGroup, pathname: string): boolean {
  return g.path === pathname || Boolean(g.items?.some((i) => i.path === pathname));
}

function Sidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const me = useMe();
  const mahallu = me.data?.mahallu;
  const profile = useProfile();
  const mark = mahallu && profile.data?.markHash ? markUrl(mahallu.slug, profile.data.markHash) : null;
  const [open, setOpen] = useState<Record<string, boolean>>({ 'nav.register': true });

  return (
    <aside className="side" aria-label="Main navigation">
      <div className="side-brand">
        <Wordmark />
      </div>
      <div className="side-head">
        {mark ? (
          <img className="tenant-mark" src={mark} alt="" />
        ) : (
          // The mahallu's own colour marks its avatar; the Rabita One frame around it keeps the product brand.
          <div className="tenant-av" aria-hidden="true" style={profile.data?.colour ? { background: profile.data.colour, color: '#fff' } : undefined}>
            {initials(mahallu?.nameEn ?? 'M')}
          </div>
        )}
        <div>
          <b>{mahallu?.nameEn ?? t('app.name')}</b>
          {mahallu?.nameMl ? <span className="ml">{mahallu.nameMl}</span> : null}
        </div>
      </div>
      <nav className="side-list">
        {NAV.map((section) => (
          <div key={section.section}>
            <div className="acc-sec">{t(section.section)}</div>
            {section.groups.map((g) => {
              const current = groupHolds(g, pathname);
              if (!g.items) {
                return (
                  <NavLink key={g.key} to={g.path ?? '/'} end className={`acc${current ? ' current' : ''}`}>
                    <Icon name={g.icon} />
                    <span>{t(g.key)}</span>
                  </NavLink>
                );
              }
              const isOpen = open[g.key] ?? current;
              return (
                <div key={g.key}>
                  <button
                    type="button"
                    className={`acc${isOpen ? ' open' : ''}${current ? ' current' : ''}`}
                    aria-expanded={isOpen}
                    onClick={() => setOpen((o) => ({ ...o, [g.key]: !isOpen }))}
                  >
                    <Icon name={g.icon} />
                    <span>{t(g.key)}</span>
                    <Icon name="chev" className="car" />
                  </button>
                  {isOpen && (
                    <div className="acc-body">
                      {g.items.map((item) => (
                        <NavLink key={item.key} to={item.path} className={({ isActive }) => `sub${isActive ? ' active' : ''}`}>
                          {t(item.key)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="side-foot">
        {t('app.descriptor')}
      </div>
    </aside>
  );
}

function MobileNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (
    <div className="mobnav">
      <Wordmark small />
      <label className="note" htmlFor="mobnav">
        {t('nav.overview')}
      </label>
      <select id="mobnav" value={pathname} onChange={(e) => navigate(e.target.value)}>
        {NAV.flatMap((s) => s.groups).map((g) =>
          g.items ? (
            <optgroup key={g.key} label={t(g.key)}>
              {g.items.map((i) => (
                <option key={i.key} value={i.path}>
                  {t(i.key)}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={g.key} value={g.path}>
              {t(g.key)}
            </option>
          ),
        )}
      </select>
    </div>
  );
}

/**
 * Who is signed in, as which role. The menu switches role (one at a time, ADR-0027), opens Active
 * sessions and signs out. A switch rotates the session; every screen then reloads its data.
 */
function AccountMenu() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!me.data) return null;
  const { user, role, roles } = me.data;

  const switchTo = async (next: Role) => {
    setBusy(true);
    setError('');
    try {
      const step = await auth.switchRole(next);
      setOpen(false);
      qc.clear();
      navigate(step.status === 'MFA_REQUIRED' ? '/sign-in' : '/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('table.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acct" ref={ref}>
      <button type="button" className="who-am-i" aria-haspopup="menu" aria-expanded={open} title={`${user.displayName} · ${user.destination}`} onClick={() => setOpen((o) => !o)}>
        <div className="avatar">{initials(user.displayName)}</div>
        <div className="who-text">
          <b>{user.displayName}</b>
          <span>{role ? t(`role.${role}`) : ''}</span>
        </div>
        <Icon name="chev" className="car" />
      </button>
      {open ? (
        <div className="acct-menu" role="menu" aria-label={t('header.account')}>
          <div className="acct-head">
            <b>{user.displayName}</b>
            <span className="note">{user.destination}</span>
          </div>
          {roles.length > 1 ? (
            <div className="acct-sec" role="group" aria-label={t('header.actAs')}>
              <span className="acct-label">{t('header.actAs')}</span>
              {roles.map((r) => (
                <button key={r} type="button" role="menuitemradio" aria-checked={r === role} className="acct-item" disabled={busy || r === role} onClick={() => void switchTo(r)}>
                  <Icon name={r === role ? 'check' : 'users'} />
                  {t(`role.${r}`)}
                </button>
              ))}
              {error ? <p className="form-error">{error}</p> : null}
            </div>
          ) : null}
          <div className="acct-sec">
            <Link to="/account/sessions" role="menuitem" className="acct-item" onClick={() => setOpen(false)}>
              <Icon name="phone" />
              {t('header.sessions')}
            </Link>
            <SignOutButton className="acct-item" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Header() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { group, leaf } = findNav(pathname);
  return (
    <header className="hdr">
      <div className="crumb">
        {group && leaf ? <>{t(group.key)} / </> : null}
        <b>{leaf ? t(leaf.key) : group ? t(group.key) : t('app.name')}</b>
      </div>
      <div className="spacer" />
      <span className="search-pill">
        <Icon name="search" />
        {t('header.search')}
      </span>
      <div className="lang" role="group" aria-label={t('header.language')}>
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            type="button"
            lang={l.code}
            className={l.code === 'ml' ? 'ml' : undefined}
            aria-pressed={i18n.language === l.code}
            onClick={() => setLanguage(l.code)}
          >
            {l.label}
          </button>
        ))}
      </div>
      <NotificationBell />
      <AccountMenu />
    </header>
  );
}

export function Shell() {
  const { t } = useTranslation();
  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        {import.meta.env.DEV ? (
          <div className="devbar" role="status">
            {t('app.devBanner')}
          </div>
        ) : null}
        <Header />
        <MobileNav />
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Header bell (pilot slice 10): unread count; opens Notifications. */
function NotificationBell() {
  const { t } = useTranslation();
  const list = useNotifications();
  const unread = list.data?.unread ?? 0;
  return (
    <Link className="hdr-btn bell" to="/notifications" title={t('header.notifications')} aria-label={unread ? t('notif.unread', { n: String(unread) }) : t('header.notifications')}>
      <Icon name="bell" />
      {unread ? <span className="bell-count num">{unread > 9 ? '9+' : unread}</span> : null}
    </Link>
  );
}
